/**
 * Que forma um autotile assume, dada a vizinhança.
 *
 * O acervo guarda, para cada casa, o autotile E a forma: o número da forma diz
 * quais dos quatro quadrantes são miolo, borda ou quina. Quem monta o mapa no
 * RPG Maker nunca escolhe isso — o editor calcula ao pintar, olhando as oito
 * casas ao redor. Para o editor daqui pintar terreno de verdade, a conta
 * precisa existir deste lado.
 *
 * As posições saem das mesmas tabelas que o desenho usa, e a forma é encontrada
 * procurando na tabela a entrada que bate com os quatro quadrantes calculados.
 * Derivar da tabela, em vez de decorar 256 combinações, é o que garante que
 * desenho e cálculo não possam discordar.
 */

import {
  FLOOR_AUTOTILE_TABLE,
  WALL_AUTOTILE_TABLE,
  type Quadrant,
  type ShapeTable,
} from './autotile-tables.js';
import { isTileA1, isTileA2, isTileA3, isTileA4, isAutotile, autotileKind } from './tiles.js';

/** As oito casas ao redor: verdadeiro quando são do mesmo autotile. */
export interface Neighbors {
  readonly n: boolean;
  readonly s: boolean;
  readonly e: boolean;
  readonly w: boolean;
  readonly nw: boolean;
  readonly ne: boolean;
  readonly sw: boolean;
  readonly se: boolean;
}

const key = (q: readonly Quadrant[]): string => q.map(([a, b]) => `${a},${b}`).join('|');

function reverseTable(table: ShapeTable): ReadonlyMap<string, number> {
  const map = new Map<string, number>();
  table.forEach((quadrants, shape) => {
    const k = key(quadrants);
    // A primeira forma vence: nas tabelas do acervo há entradas repetidas, e a
    // de índice menor é a canônica que o editor original emite.
    if (!map.has(k)) map.set(k, shape);
  });
  return map;
}

const FLOOR_BY_QUADRANTS = reverseTable(FLOOR_AUTOTILE_TABLE);

/**
 * O bloco de um autotile de piso tem 2×3 tiles, ou 4×6 quadrantes:
 *
 * - a coluna 0 é a borda esquerda e a 3 a direita; as colunas 1 e 2 são miolo
 *   (cruzadas: a metade esquerda do tile vem da coluna 2 e a direita da 1);
 * - a linha 2 é a borda de cima e a 5 a de baixo; as linhas 3 e 4 são miolo;
 * - as linhas 0 e 1 das colunas 2 e 3 guardam as quinas côncavas, usadas quando
 *   os dois lados encostam mas a diagonal não.
 */
function floorQuadrants(nb: Neighbors): Quadrant[] {
  const corner = (
    side1: boolean,
    side2: boolean,
    diagonal: boolean,
    outerCol: number,
    innerCol: number,
    outerRow: number,
    innerRow: number,
    concave: Quadrant,
  ): Quadrant =>
    side1 && side2 && !diagonal
      ? concave
      : [side1 ? innerCol : outerCol, side2 ? innerRow : outerRow];

  return [
    corner(nb.w, nb.n, nb.nw, 0, 2, 2, 4, [2, 0]),
    corner(nb.e, nb.n, nb.ne, 3, 1, 2, 4, [3, 0]),
    corner(nb.w, nb.s, nb.sw, 0, 2, 5, 3, [2, 1]),
    corner(nb.e, nb.s, nb.se, 3, 1, 5, 3, [3, 1]),
  ];
}

/** A forma de um autotile de piso (A1 e A2). */
export function floorShape(nb: Neighbors): number {
  return FLOOR_BY_QUADRANTS.get(key(floorQuadrants(nb))) ?? 0;
}

/**
 * A forma de um autotile de parede (A3 e a metade ímpar da A4).
 *
 * Parede não tem quina côncava: só importam os quatro lados, e cada lado solto
 * acende um bit. A tabela do acervo é exatamente essa contagem.
 */
export function wallShape(nb: Neighbors): number {
  const shape = (nb.w ? 0 : 1) | (nb.n ? 0 : 2) | (nb.e ? 0 : 4) | (nb.s ? 0 : 8);
  return shape < WALL_AUTOTILE_TABLE.length ? shape : 0;
}

/** Autotiles da A4 alternam faixas de piso e de parede, linha sim, linha não. */
export function usesWallTable(tileId: number): boolean {
  if (isTileA3(tileId)) return true;
  if (!isTileA4(tileId)) return false;
  return Math.floor(autotileKind(tileId) / 8) % 2 === 1;
}

/**
 * O identificador final do tile: mesmo autotile, forma calculada da vizinhança.
 *
 * Um tile que não é autotile não tem forma — volta como está.
 */
export function shapedTileId(tileId: number, nb: Neighbors): number {
  if (!isAutotile(tileId)) return tileId;
  const base = tileId - ((tileId - 2048) % 48);
  const shape = usesWallTable(tileId) ? wallShape(nb) : floorShape(nb);
  return base + shape;
}

/** Autotiles só se juntam com o mesmo tipo — água não casa com grama. */
export function sameFamily(a: number, b: number): boolean {
  if (!isAutotile(a) || !isAutotile(b)) return a === b;
  return autotileKind(a) === autotileKind(b);
}

export { isTileA1, isTileA2 };
